export type PopupClient = 'ahv' | 'contactInformation' | 'pendingWalletActivation' | 'applePay' | 'pushNotification';

type PopupClientConfig = Record<
    PopupClient,
    {
        pri: number;
        alwaysShow: boolean;
        waitFor: 'always' | 'if native' | 'never';
    }
>;

const popupClientConfig: PopupClientConfig = {
    ahv: { pri: 1, alwaysShow: true, waitFor: 'always' },
    pendingWalletActivation: { pri: 2, alwaysShow: true, waitFor: 'if native' },
    contactInformation: { pri: 3, alwaysShow: false, waitFor: 'never' },
    applePay: { pri: 4, alwaysShow: true, waitFor: 'never' },
    pushNotification: { pri: 5, alwaysShow: false, waitFor: 'never' },
};

const TEN_MINUTES = 10 * 60 * 1000;
const DEFAULT_PRIORITY = 99;

type PopupManagerOptions = {
    session?: Storage;
    isNative?: () => boolean;
    disableWaitingForClients?: boolean;
};

export const initPopupManager = ({
    session = sessionStorage,
    isNative = () => !!window.ReactNativeWebView,
    disableWaitingForClients = false,
}: PopupManagerOptions = {}) => {
    const popupQueue = window.popupQueue ?? (window.popupQueue = new Map());

    const getPriority = (type: string): number => popupClientConfig[type as PopupClient]?.pri ?? DEFAULT_PRIORITY;
    const isAlwaysShow = (type: string) => popupClientConfig[type as PopupClient]?.alwaysShow ?? false;

    const popupAlreadyShown = () => {
        const lastPopupTimestamp = session.getItem('popupShownTimestamp');
        if (!lastPopupTimestamp) return false;

        // popup will be shown again after 10 minutes. Best effort. Should connect it to the session somehow.
        return Date.now() - parseInt(lastPopupTimestamp, 10) < TEN_MINUTES;
    };

    const readyForPopup = () => {
        if (disableWaitingForClients) return true;

        const waitForAlways = Object.keys(popupClientConfig).filter(
            (key) => popupClientConfig[key as PopupClient].waitFor === 'always',
        );
        const waitForIfNative = Object.keys(popupClientConfig).filter(
            (key) => popupClientConfig[key as PopupClient].waitFor === 'if native',
        );

        const allRequiredClientsReported = waitForAlways.every((key) => popupQueue.has(key));
        const allRequiredNativeClientsReported = waitForIfNative.every((key) => popupQueue.has(key));

        return allRequiredClientsReported && (isNative() ? allRequiredNativeClientsReported : true);
    };

    type PopupEntry = { key: string; popup: { requestedPopup: boolean; successCb?: () => void } };

    const displayAlwaysShowPopups = (alwaysShowPopups: PopupEntry[]) => {
        alwaysShowPopups.forEach(({ key, popup }) => {
            const { successCb = () => {} } = popup;
            successCb();
            popupQueue.delete(key);
        });
        return alwaysShowPopups.length > 0;
    };

    const displayRegularPopup = (regularPopups: PopupEntry[]) => {
        if (regularPopups.length === 0) return false;

        const {
            key,
            popup: { successCb = () => {} },
        } = regularPopups[0];
        successCb();
        popupQueue.delete(key);
        return true;
    };

    const processPopupQueue = () => {
        if (popupQueue.size === 0 || !readyForPopup()) return;

        const allPopups: PopupEntry[] = [...popupQueue.entries()]
            .filter(([, popup]) => popup.requestedPopup)
            .map(([key, popup]) => ({ key, popup }))
            .sort((a, b) => getPriority(a.key) - getPriority(b.key));

        if (allPopups.length === 0) return;

        const alwaysShowPopups = allPopups.filter(({ key }) => isAlwaysShow(key));
        const regularPopups = allPopups.filter(({ key }) => !isAlwaysShow(key));

        const alwaysShowPopupsDisplayed = displayAlwaysShowPopups(alwaysShowPopups);
        if (alwaysShowPopupsDisplayed) {
            session.setItem('popupShownTimestamp', Date.now().toString());
        }

        if (!popupAlreadyShown()) {
            const regularPopupDisplayed = displayRegularPopup(regularPopups);
            if (regularPopupDisplayed) {
                session.setItem('popupShownTimestamp', Date.now().toString());
            }
        }
    };

    window.addEventListener('popupQueueUpdated', processPopupQueue);

    // run in case some webapp requested a popup before the event listener was registered
    processPopupQueue();

    return () => {
        window.removeEventListener('popupQueueUpdated', processPopupQueue);
        window.popupQueue = new Map();
    };
};

export default initPopupManager;
